import { defineComponent, h, reactive, ref } from "vue";

export const UiTabView = defineComponent({
  name: "TabView",
  setup(_, { slots }) {
    const state = reactive({ active: 0 });
    return () => {
      const panels = (slots.default?.() || []).filter(
        (node) => (node.type as any)?.name === "TabPanel",
      );
      return h(
        "div",
        {
          class:
            "rounded-lg border border-slate-200 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.07)]",
        },
        [
          h(
            "div",
            {
              class:
                "flex flex-wrap gap-2 border-b border-slate-200 bg-slate-50/90 p-4",
            },
            panels.map((panel, index) =>
              h(
                "button",
                {
                  class: [
                    "min-h-[38px] rounded-lg px-4 py-2 text-[15px] font-bold transition",
                    state.active === index
                      ? "bg-teal-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:bg-teal-50",
                  ],
                  onClick: () => (state.active = index),
                },
                (panel.props as any)?.header,
              ),
            ),
          ),
          h(
            "div",
            { class: "bg-white p-4" },
            (panels[state.active]?.children as any)?.default?.(),
          ),
        ],
      );
    };
  },
});

export const UiTabPanel = defineComponent({
  name: "TabPanel",
  props: { header: String },
  setup(_, { slots }) {
    return () => slots.default?.();
  },
});

export const UiAccordion = defineComponent({
  name: "Accordion",
  props: { activeIndex: [Number, Array], multiple: Boolean },
  setup(props, { slots }) {
    const open = ref<number[]>(
      Array.isArray(props.activeIndex)
        ? props.activeIndex
        : [Number(props.activeIndex || 0)],
    );
    return () => {
      const panels = (slots.default?.() || []).filter(
        (node) => (node.type as any)?.name === "AccordionTab",
      );
      return h(
        "div",
        { class: "space-y-3" },
        panels.map((panel, index) => {
          const isOpen = open.value.includes(index);
          return h(
            "div",
            {
              class:
                "overflow-visible rounded-lg border border-slate-200 bg-white shadow-[0_8px_22px_rgba(15,23,42,0.06)] transition-colors duration-200",
            },
            [
              h(
                "button",
                {
                  class: [
                    "group flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[15px] font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-200",
                    isOpen
                      ? "bg-teal-50 text-teal-900"
                      : "bg-slate-50 text-slate-800 hover:bg-slate-100",
                  ],
                  onClick: () => {
                    open.value = props.multiple
                      ? isOpen
                        ? open.value.filter((i) => i !== index)
                        : [...open.value, index]
                      : [index];
                  },
                },
                [
                  h("span", { class: "min-w-0 truncate" }, (panel.props as any)?.header),
                  h(
                    "span",
                    {
                      class: [
                        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs transition-all duration-200",
                        isOpen
                          ? "border-teal-200 bg-teal-600 text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-500 group-hover:border-teal-200 group-hover:text-teal-700",
                      ],
                    },
                    [
                      h("i", {
                        class: [
                          "pi pi-chevron-down text-[11px] transition-transform duration-300 ease-out",
                          isOpen ? "rotate-180" : "rotate-0",
                        ],
                      }),
                    ],
                  ),
                ],
              ),
              h(
                "div",
                {
                  class: [
                    "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                  ],
                },
                [
                  h(
                    "div",
                    { class: isOpen ? "min-h-0 overflow-visible" : "min-h-0 overflow-hidden" },
                    h(
                      "div",
                      { class: "border-t border-slate-100 p-4" },
                      (panel.children as any)?.default?.(),
                    ),
                  ),
                ],
              ),
            ],
          );
        }),
      );
    };
  },
});

export const UiAccordionTab = defineComponent({
  name: "AccordionTab",
  props: { header: String },
  setup(_, { slots }) {
    return () => slots.default?.();
  },
});

export const UiTabMenu = defineComponent({
  name: "TabMenu",
  setup:
    (_, { slots }) =>
    () =>
      h("div", slots.default?.()),
});
export const UiMenubar = UiTabMenu;
